import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { useSignalsSummary } from '../hooks/useSignals';

/**
 * Top-bar "what needs attention" affordance. Reuses the deterministic Signals
 * summary (Wave 0) to show the count of open next-best-actions and deep-links to
 * the home command center where the full ranked feed lives. This is the always-
 * visible entry point that ties every domain page back to the signals layer —
 * the connectedness thesis: highly connected, not loosely separated.
 */
export function SignalsBell() {
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useSignalsSummary(wsId);

  const total = data?.total ?? 0;
  const hasHigh = (data?.high ?? 0) > 0;
  const label = total > 0 ? t('topbar.signalsHint', { count: total }) : t('topbar.signalsNone');

  return (
    <button
      type="button"
      onClick={() => navigate(`/app/workspace/${wsId}/home`)}
      aria-label={`${t('topbar.signals')} — ${label}`}
      title={label}
      className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800"
    >
      <Sparkles size={17} />
      {total > 0 && (
        <span
          className={
            'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white ' +
            (hasHigh ? 'bg-warn-600' : 'bg-accent-600')
          }
        >
          {total > 99 ? '99+' : total}
        </span>
      )}
    </button>
  );
}
